// src/types/index.ts
export interface Location {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp?: number;
}

// Content parts for multimodal messages
export interface TextPart {
  type: "text";
  content: string;
}

export interface ImagePart {
  type: "image";
  url: string;
  alt?: string;
  metadata?: {
    size?: number;
    format?: string;
    width?: number;
    height?: number;
  };
}

export interface AudioPart {
  type: "audio";
  url: string;
  duration?: number;
  transcript?: string;
}

// ── System-message parts ──
//
// Written by Cloud Functions, not by the user or the chat AI.
// MessageBubble intercepts role==='system' messages with these
// parts and renders dedicated full-width cards instead of the
// standard speech bubble. See
// src/components/collections/CollectionRevealCard.tsx and
// functions2/src/collections/revealWriter.ts.
export interface CollectionRevealPart {
  type: "collection_reveal";
  data: {
    collection_id: string;
    collection_ref_path: string;
    collection_kind: "canonical" | "personal";
    collection_title: string;
    item_id: string;
    item_title: string;
    found_count_at_find: number;
    collection_total_items: number;
    collection_hidden_total: boolean;
    collection_cover_image_url?: string;
    photo_reference?: string;
    image_url?: string;
    reveal_prose: string;
    reveal_follow_up?: string;
    completed: boolean;
    find_id: string;
  };
}

export interface CollectionCompletionPart {
  type: "collection_completion";
  data: {
    collection_id: string;
    collection_title: string;
    total_items: number;
    completion_postcard_id?: string;
  };
}

export type ContentPart =
  | TextPart
  | ImagePart
  | AudioPart
  | CollectionRevealPart
  | CollectionCompletionPart;

export interface Message {
  id: string;
  /** 'system' messages are written by Cloud Functions (collection
   *  reveals, completion celebrations, etc.) — never by the user
   *  or the chat AI. The renderer intercepts them and emits
   *  dedicated cards instead of the standard speech bubble. */
  role: "user" | "assistant" | "system";
  parts?: ContentPart[]; // Make parts optional for backward compatibility
  content?: string; // Legacy field for backward compatibility
  image_url?: string; // Legacy field for backward compatibility
  audio_url?: string; // Legacy field for backward compatibility
  created_at: Date;
  location?: {
    geohash: string;
    precision: "coarse" | "medium" | "fine";
  };
  metadata?: {
    model_version?: string;
    processing_time?: number;
    tokens_used?: number;
  };
  audioGenerating?: boolean;
  /** Set by the audio Cloud Function when generation errors out
   *  (TTS API failure, storage upload failure, etc.). The
   *  message renderer reads this to swap the infinite
   *  "Generating audio narration..." spinner for an explicit
   *  "audio unavailable" affordance. Without this flag, a
   *  failed generation left the chip spinning forever because
   *  the spinner is gated on `parts` containing an audio entry,
   *  not on `audioGenerating`. */
  audioGenerationFailed?: boolean;
  /** Short error string from the failure path. Truncated to
   *  200 chars on the server. Surface only to debug builds —
   *  end users get a generic message. */
  audioFailureReason?: string;
  isStreaming?: boolean;
}

export interface Conversation {
  id: string;
  user_id: string;
  title: string;
  created_at: Date;
  updated_at: Date;
  message_count: number;
  location_data: string[];
  is_public: boolean;
  tags: string[];
}

export interface TourGuideRequest {
  message: string;
  image?: string;
  location?: Location;
  conversation_id: string;
}

export interface StreamResponse {
  content: string;
  audio_url?: string;
  image_urls?: string[];
  done: boolean;
}

export interface GeolocationError {
  code: number;
  message: string;
}

export interface CameraError {
  name: string;
  message: string;
}

// Authentication types
export interface AuthUser {
  uid: string;
  email?: string;
  displayName?: string;
  emailVerified: boolean;
  isAnonymous: boolean;
}

export interface UserProfile {
  uid: string;
  email?: string;
  displayName?: string;
  emailVerified: boolean;
  userType: "anonymous" | "registered";
  createdAt: Date;
  lastActive: Date;
  preferences: {
    locationPrivacy: "coarse" | "precise";
    theme: "light" | "dark" | "system";
    language: string;
  };
  gdprConsent: {
    necessary: boolean;
    functional: boolean;
    analytics: boolean;
    marketing: boolean;
    consentDate: Date;
    privacyPolicyVersion: string;
  };
}

// GDPR types
export interface GDPRConsent {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  consentDate: Date;
  privacyPolicyVersion: string;
}

export interface DataExportRequest {
  id: string;
  user_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  requested_at: Date;
  completed_at?: Date;
  download_url?: string;
  expires_at?: Date;
}

export interface DataDeletionRequest {
  id: string;
  user_id: string;
  status: "pending" | "processing" | "completed" | "failed";
  requested_at: Date;
  completed_at?: Date;
  deletion_type: "partial" | "complete";
}

// UI types
export interface SidebarState {
  isOpen: boolean;
  width: number;
}

export interface ModalState {
  authModal: boolean;
  settingsModal: boolean;
  privacyModal: boolean;
  cameraModal: boolean;
}

// Legacy types for backward compatibility
export type { Message as LegacyMessage };

// Helper function to convert legacy messages to new format
export const convertLegacyMessage = (legacy: any): Message => {
  const parts: ContentPart[] = [];

  // Add text content
  if (legacy.content) {
    parts.push({
      type: "text",
      content: legacy.content,
    });
  }

  // Add image if present
  if (legacy.image_url) {
    parts.push({
      type: "image",
      url: legacy.image_url,
      alt: "Attached image",
    });
  }

  // Add audio if present
  if (legacy.audio_url) {
    parts.push({
      type: "audio",
      url: legacy.audio_url,
    });
  }

  return {
    id: legacy.id,
    role: legacy.role,
    parts,
    created_at: legacy.created_at,
    location: legacy.location,
    metadata: legacy.metadata,
  };
};

// Helper function to extract text content from message
export const getMessageText = (message: Message): string => {
  // Handle both new format (parts) and legacy format (content)
  if (message.parts) {
    const textParts = message.parts.filter(
      (part): part is TextPart => part.type === "text"
    );
    return textParts.map((part) => part.content).join("\n");
  }

  // Legacy format fallback
  return message.content || "";
};

// Helper function to get images from message
export const getMessageImages = (message: Message): ImagePart[] => {
  // Handle both new format (parts) and legacy format (image_url)
  if (message.parts) {
    return message.parts.filter(
      (part): part is ImagePart => part.type === "image"
    );
  }

  // Legacy format fallback
  if (message.image_url) {
    return [
      {
        type: "image",
        url: message.image_url,
        alt: "Attached image",
      },
    ];
  }

  return [];
};

// Helper function to get audio from message
export const getMessageAudio = (message: Message): AudioPart[] => {
  // Handle both new format (parts) and legacy format (audio_url)
  if (message.parts) {
    return message.parts.filter(
      (part): part is AudioPart => part.type === "audio"
    );
  }

  // Legacy format fallback
  if (message.audio_url) {
    return [
      {
        type: "audio",
        url: message.audio_url,
      },
    ];
  }

  return [];
};

// Re-export profile types
export * from "./profile";
